import { getCustomListControllerFetchCustomListByPublicIdQueryOptions } from '@/api/generated/okami.ts'
import { Avatar, AvatarImage } from '@/components/ui/avatar.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import { parseDistanceByDate, parseTagColor } from '@/utils/helpers.ts'
import { createFileRoute } from '@tanstack/react-router'
import { getYear } from 'date-fns'
import { Clock } from 'lucide-react'

export const Route = createFileRoute('/public/share-collection/$publicId')({
  component: ShareCollection,
  notFoundComponent: ShareCollectionNotFound,
  loader: async ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      getCustomListControllerFetchCustomListByPublicIdQueryOptions(params.publicId),
    )
  },
  head: ({ loaderData }) => {
    return {
      meta: [
        {
          title: `${loaderData?.title} | Okami`,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:title',
          name: 'description',
          content: loaderData?.description,
        },

        {
          property: 'twitter:card',
          content: 'summary',
        },
        {
          property: 'twitter:title',
          content: loaderData?.title,
        },
      ],
    }
  },
})

function ShareCollectionNotFound() {
  return (
    <section className="flex flex-1 w-full h-screen justify-center items-center flex-col gap-4">
      <picture className="size-[200px] ">
        <img src="/okami-logo.png" alt="404" />
      </picture>
      <h1 className="text-6xl text-center text-foreground mt-5">Ops...</h1>
      <h2 className="text-4xl text-muted-foreground">Coleção não encontrada</h2>
    </section>
  )
}

function ShareCollection() {
  const { title, description, works, owner } = Route.useLoaderData()

  function handleOpenUrl(workUrl: string) {
    window.open(workUrl, '_blank')
  }

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">{title}</h1>
            <p className="text-md text-muted-foreground">{description}</p>
          </div>

          <div className="flex flex-col gap-3 justify-center items-center">
            <div className="flex items-center gap-2">
              <div className="flex-col items-end hidden md:flex">
                <span className="text-foreground text-md">{owner.name}</span>
                <p className="text-sm text-muted-foreground">Membro de desde {getYear(owner.createdAt)}</p>
              </div>

              <Avatar className="size-12">
                <AvatarImage src={owner.imageUrl ?? '/okami-logo.png'} />
              </Avatar>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {works.map((work) => (
            <Card key={work.id}>
              <CardContent className="p-0">
                <div className="relative aspect-[3/4] overflow-hidden rounded-md">
                  <img
                    src={work.imageUrl || '/okami-logo.png'}
                    alt={work.name}
                    className="object-cover transition-transform hover:scale-110 h-[360px]"
                  />
                </div>

                <div className="p-4 flex flex-col gap-2 justify-center">
                  <h3 className="font-semibold text-foreground text-sm mb-3 line-clamp-2 leading-tight text-center">
                    {work.name}
                  </h3>
                  <div className="gap-0.5 flex flex-wrap justify-center">
                    {work.tags.slice(0, 4)?.map((tag) => {
                      const color = parseTagColor(tag.color)

                      return (
                        <Badge
                          className="text-gray-100"
                          style={{ background: color[600] }}
                          variant="outline"
                          key={tag.id}
                        >
                          {tag.name}
                        </Badge>
                      )
                    })}
                  </div>

                  {/* Last Update */}
                  <div className="flex items-center gap-1 text-muted-foreground text-xs justify-center">
                    <Clock className="size-3" />
                    <span className="text-muted-foreground text-center">
                      Atualizado {parseDistanceByDate(work.updatedAt)}
                    </span>
                  </div>

                  <Button variant="link" className="mt-2" onClick={() => handleOpenUrl(work.url)}>
                    <a href={`/public/share/${work.id}`} className="text-blue-500 hover:underline">
                      {work.category === 'ANIME' ? 'Ver Anime' : 'Ver Manga'}
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#1e2832] mt-12">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-gray-500 text-sm">
            Coleção compartilhada • Visualização somente leitura
          </p>
          <p className="text-center text-gray-500 text-sm mt-2">
            By{' '}
            <a href="https://go.myokami.xyz" className="font-bold underline">
              Okami
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
